/**
 * Aufgabe Detailansicht — Große Ansicht mit Tabs
 * - Stammdaten (rechts oben)
 * - Tabs: Verlauf | Unterlagen | Verknüpfte Aufgaben
 * - Brief-System Integration
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTabParams } from '@/hooks/useTabRoute';
import { useTabs } from '@/contexts/TabContext';
import UnteraufgabenKarte from '@/components/UnteraufgabenKarte';
import AutopilotBefundKarte from '@/components/AutopilotBefundKarte';
import {
  ArrowLeft, Edit, Clock, Plus, Send, Trash2,
  Calendar, User, Briefcase, AlertCircle, CheckCircle, FileText,
  Link2, MessageSquare, Upload, ChevronRight,
  RefreshCw, Save, X, Download, BookOpen, RotateCcw, Eye, Pencil, Mail,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { aufgabeLadefehlerArt, ladeAufgabeMitWiederholung as _ladeMitWiederholung } from '@/lib/aufgabeLaden';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { betreuungAPI } from '@/api/betreuungAPI';
import { toast } from 'sonner';
import { schreibstubeAPI } from '@/api/schreibstubeAPI';
import { schreibstubeUrl } from '@/pages/schreibstube/schreibstube';
import { holePreviewUrl } from '@/lib/downloadTicket';

const API = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

const PRIO_CONFIG = {
  dringend: { label: 'Dringend', color: 'bg-red-500 text-white' },
  hoch: { label: 'Hoch', color: 'bg-orange-400 text-white' },
  mittel: { label: 'Mittel', color: 'bg-blue-100 text-blue-700' },
  normal: { label: 'Normal', color: 'bg-blue-100 text-blue-700' },
  niedrig: { label: 'Niedrig', color: 'bg-green-100 text-green-700' },
};

const STATUS_CONFIG = {
  offen: { label: 'Offen', color: 'bg-amber-100 text-amber-800', icon: AlertCircle },
  in_bearbeitung: { label: 'In Bearbeitung', color: 'bg-blue-100 text-blue-800', icon: Clock },
  erledigt: { label: 'Erledigt', color: 'bg-green-100 text-green-800', icon: CheckCircle },
};

export default function AufgabeDetailPage() {
  const { id } = useTabParams();
  const navigate = useNavigate();
  const { openTab } = useTabs();
  const token = localStorage.getItem('olympus_token');
  const authHeaders = { 'Authorization': `Bearer ${token}` };
  const [aufgabe, setAufgabe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ladefehler, setLadefehler] = useState(null);   // {art: 'nicht_gefunden'|'nicht_erreichbar', status}
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);

  // Verlauf
  const [verlauf, setVerlauf] = useState([]);
  const [verlaufText, setVerlaufText] = useState('');
  const [verlaufDatum, setVerlaufDatum] = useState(new Date().toISOString().slice(0, 10));
  const [addingVerlauf, setAddingVerlauf] = useState(false);

  // Unterlagen
  const [dokumente, setDokumente] = useState([]);
  const [briefe, setBriefe] = useState([]);
  const [dateiReferenzen, setDateiReferenzen] = useState([]);
  const [showDateiLink, setShowDateiLink] = useState(false);
  const [ordnerListe, setOrdnerListe] = useState([]);
  const [linkOrdner, setLinkOrdner] = useState('');
  const [ordnerDateien, setOrdnerDateien] = useState([]);

  // Verknüpfte Aufgaben
  const [verknuepfte, setVerknuepfte] = useState([]);
  const [alleAufgaben, setAlleAufgaben] = useState([]);
  const [linkQuery, setLinkQuery] = useState('');

  // PDF-Vorschau
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewName, setPreviewName] = useState('');

  // Umbenennen
  const [renamingIdx, setRenamingIdx] = useState(null);
  const [newName, setNewName] = useState('');
  const [showLinkSearch, setShowLinkSearch] = useState(false);

  // Brief-Editor
  const [vorgaenge, setVorgaenge] = useState([]);
  const [activeTab, setActiveTab] = useState('verlauf');

  const loadAufgabe = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setLadefehler(null);
    try {
      const data = await _ladeMitWiederholung(id, (x) => betreuungAPI.getAufgabe(x));
      setAufgabe(data);
      setEditData(data);

      // Verlauf laden
      try {
        const vr = await fetch(`${API}/api/betreuung/aufgaben/${id}/verlauf`).then(r => r.json());
        setVerlauf(Array.isArray(vr) ? vr : vr.verlauf || []);
      } catch { setVerlauf([]); }

      // Dokumente laden - aufgabenrelevante + verknüpfte
      if (data.klient_id) {
        try {
          let allDocs = [];
          // 1. Dokumente mit aufgabe_id laden
          const dr1 = await betreuungAPI.listDokumente({ aufgabe_id: id });
          const aufgabeDocs = Array.isArray(dr1) ? dr1 : dr1.dokumente || [];
          allDocs = [...aufgabeDocs];

          // 2. Verknüpfte Dokumente laden (via dokument_ids)
          try {
            const linkedDocs = await betreuungAPI.listAufgabeDokumente(id);
            const existIds = new Set(allDocs.map(d => d.id));
            (Array.isArray(linkedDocs) ? linkedDocs : []).forEach(d => {
              if (!existIds.has(d.id)) allDocs.push(d);
            });
          } catch {}

          // 3. Dokumente mit vorgang_id laden (falls vorhanden)
          if (data.vorgang_id) {
            const dr2 = await betreuungAPI.listDokumente({ vorgang_id: data.vorgang_id });
            const vorgangDocs = Array.isArray(dr2) ? dr2 : dr2.dokumente || [];
            const existIds2 = new Set(allDocs.map(d => d.id));
            vorgangDocs.forEach(d => { if (!existIds2.has(d.id)) allDocs.push(d); });
          }

          setDokumente(allDocs);
        } catch { setDokumente([]); }

        // Briefe laden
        try {
          const br = await fetch(`${API}/api/briefe/klient/${data.klient_id}`).then(r => r.json());
          const aufgabeBriefe = (br.briefe || []).filter(b => b.aufgabe_id === id);
          setBriefe(aufgabeBriefe);
        } catch { setBriefe([]); }

        // Vorgänge laden
        try {
          const vr = await fetch(`${API}/api/betreuung/klient-vorgaenge/${data.klient_id}`).then(r => r.json());
          setVorgaenge(Array.isArray(vr) ? vr : vr.vorgaenge || []);
        } catch { setVorgaenge([]); }
      }

      // Verknüpfte Aufgaben laden
      try {
        const links = data.verknuepfte_aufgaben || [];
        if (links.length > 0) {
          const all = await betreuungAPI.listAufgaben({});
          const linked = (Array.isArray(all) ? all : []).filter(a => links.includes(a.id));
          setVerknuepfte(linked);
        }
      } catch { setVerknuepfte([]); }

      // Physische Datei-Referenzen laden
      setDateiReferenzen(data.datei_referenzen || []);

      // Ordner für Datei-Link-Dialog laden
      if (data.klient_id) {
        try {
          const ordnerRes = await fetch(`${API}/api/akten-ordner/liste/${data.klient_id}`, { headers: { 'Authorization': `Bearer ${token}` } }).then(r => r.json());
          setOrdnerListe(ordnerRes.ordner || []);
        } catch { setOrdnerListe([]); }
      }
    } catch (e) {
      const art = aufgabeLadefehlerArt(e);
      setLadefehler({ art, status: e?.status || null });
      toast.error(art === 'nicht_gefunden' ? 'Aufgabe nicht gefunden' : 'Aufgabe konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { loadAufgabe(); }, [loadAufgabe]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await betreuungAPI.updateAufgabe(id, editData);
      toast.success('Aufgabe aktualisiert');
      setEditing(false);
      loadAufgabe();
    } catch {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await betreuungAPI.updateAufgabe(id, { status: newStatus });
      toast.success(`Status: ${STATUS_CONFIG[newStatus]?.label || newStatus}`);
      loadAufgabe();
    } catch {
      toast.error('Fehler');
    }
  };

  // Verlauf hinzufügen
  const addVerlaufEintrag = async () => {
    if (!verlaufText.trim()) return;
    setAddingVerlauf(true);
    try {
      await fetch(`${API}/api/betreuung/aufgaben/${id}/verlauf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: verlaufText, datum: verlaufDatum, typ: 'manuell' }),
      });
      setVerlaufText('');
      loadAufgabe();
      toast.success('Verlaufseintrag hinzugefügt');
    } catch {
      toast.error('Fehler');
    } finally {
      setAddingVerlauf(false);
    }
  };

  // Aufgabe verknüpfen
  const linkAufgabe = async (targetId) => {
    try {
      const current = aufgabe.verknuepfte_aufgaben || [];
      if (current.includes(targetId)) return;
      await betreuungAPI.updateAufgabe(id, { verknuepfte_aufgaben: [...current, targetId] });
      // Gegenseitige Verknüpfung
      const target = await betreuungAPI.getAufgabe(targetId);
      const targetLinks = target.verknuepfte_aufgaben || [];
      if (!targetLinks.includes(id)) {
        await betreuungAPI.updateAufgabe(targetId, { verknuepfte_aufgaben: [...targetLinks, id] });
      }
      setShowLinkSearch(false);
      setLinkQuery('');
      toast.success('Aufgabe verknüpft');
      loadAufgabe();
    } catch {
      toast.error('Fehler');
    }
  };

  // Ticket-basierte Vorschau/Download-URLs (Live Preview Auth Gap, 25.09.2026):
  // ersetzt bare Backend-URLs ohne jede Auth (frueher 401 im iframe/window.open).
  const holeAktenDateiUrl = (klientId, ordner, dateiname) => holePreviewUrl(
    `${API}/api/akten-ordner/download-ticket/${klientId}/${ordner}/${encodeURIComponent(dateiname)}`,
    `${API}/api/akten-ordner/download/${klientId}/${ordner}/${encodeURIComponent(dateiname)}`,
    authHeaders
  );

  const holeBriefPdfUrl = (briefId) => holePreviewUrl(
    `${API}/api/briefe/${briefId}/pdf-ticket`,
    `${API}/api/briefe/${briefId}/pdf`,
    authHeaders
  );

  const searchAufgaben = async (q) => {
    setLinkQuery(q);
    if (q.length < 2) { setAlleAufgaben([]); return; }
    try {
      const all = await betreuungAPI.listAufgaben({});
      const filtered = (Array.isArray(all) ? all : [])
        .filter(a => a.id !== id && a.titel?.toLowerCase().includes(q.toLowerCase()));
      setAlleAufgaben(filtered.slice(0, 10));
    } catch { setAlleAufgaben([]); }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><RefreshCw className="w-8 h-8 animate-spin text-slate-400" /></div>;
  }
  if (!aufgabe) {
    if (ladefehler?.art === 'nicht_erreichbar') {
      return (
        <div className="text-center py-12 space-y-3" data-testid="aufgabe-ladefehler">
          <p className="text-slate-700">Aufgabe konnte nicht geladen werden.</p>
          <p className="text-sm text-slate-500">{(!ladefehler.status || ladefehler.status >= 500) ? 'Der Server war vorübergehend nicht erreichbar' : 'Die Anfrage wurde abgelehnt'}{ladefehler.status ? ` (HTTP ${ladefehler.status})` : ''}. Das heißt nicht, dass die Aufgabe fehlt.</p>
          <Button variant="outline" size="sm" onClick={() => loadAufgabe()} data-testid="aufgabe-erneut-laden">
            <RefreshCw className="w-4 h-4 mr-2" />Erneut laden
          </Button>
        </div>
      );
    }
    return (
      <div className="text-center py-12" data-testid="aufgabe-nicht-gefunden">
        <p className="text-slate-500">Aufgabe nicht gefunden</p>
        <p className="text-xs text-slate-400 mt-1">ID: {id || '–'}</p>
      </div>
    );
  }

  const prio = PRIO_CONFIG[aufgabe.prioritaet] || PRIO_CONFIG.normal;
  const status = STATUS_CONFIG[aufgabe.status] || STATUS_CONFIG.offen;
  const StatusIcon = status.icon;

  return (
    <div className="space-y-6" data-testid="aufgabe-detail-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div>
            <h1 className="text-xl font-bold text-slate-800">{aufgabe.titel}</h1>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <Badge className={prio.color}>{prio.label}</Badge>
              <Badge className={status.color} data-testid="aufgabe-status-badge">
                <StatusIcon className="w-3 h-3 mr-1" /> {status.label}
              </Badge>
              {aufgabe.betreuter_name && (
                <Badge variant="outline" className="text-xs">
                  <User className="w-3 h-3 mr-1" /> {aufgabe.betreuter_name}
                </Badge>
              )}
              {aufgabe.faellig_am && (
                <Badge variant="outline" className="text-xs">
                  <Calendar className="w-3 h-3 mr-1" />
                  Fällig: {new Date(aufgabe.faellig_am).toLocaleDateString('de-DE')}
                </Badge>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {aufgabe.status !== 'erledigt' ? (
            <Button size="sm" variant="outline" onClick={() => handleStatusChange('erledigt')} className="text-green-700" data-testid="aufgabe-erledigt-btn">
              <CheckCircle className="w-4 h-4 mr-1" /> Erledigt
            </Button>
          ) : (
            <Button size="sm" variant="outline" onClick={() => handleStatusChange('offen')} className="text-amber-700" data-testid="aufgabe-reopen-btn">
              <RotateCcw className="w-4 h-4 mr-1" /> Wieder öffnen
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => setEditing(!editing)} data-testid="aufgabe-edit-btn">
            <Edit className="w-4 h-4 mr-1" /> {editing ? 'Abbrechen' : 'Bearbeiten'}
          </Button>
        </div>
      </div>

      {/* Edit Form */}
      {editing && (
        <Card data-testid="aufgabe-edit-form">
          <CardContent className="pt-4 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-slate-500">Titel</label>
                <Input value={editData.titel || ''} onChange={e => setEditData(p => ({ ...p, titel: e.target.value }))} />
              </div>
              <div>
                <label className="text-xs text-slate-500">Priorität</label>
                <Select value={editData.prioritaet || 'normal'} onValueChange={v => setEditData(p => ({ ...p, prioritaet: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(PRIO_CONFIG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-slate-500">Fällig am</label>
                <Input type="date" value={editData.faellig_am || ''} onChange={e => setEditData(p => ({ ...p, faellig_am: e.target.value }))} />
              </div>
              <div>
                <label className="text-xs text-slate-500">Status</label>
                <Select value={editData.status || 'offen'} onValueChange={v => setEditData(p => ({ ...p, status: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS_CONFIG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-xs text-slate-500">Beschreibung</label>
              <Textarea rows={3} value={editData.beschreibung || ''} onChange={e => setEditData(p => ({ ...p, beschreibung: e.target.value }))} />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setEditing(false)}>Abbrechen</Button>
              <Button size="sm" onClick={handleSave} disabled={saving} data-testid="aufgabe-save-btn">
                <Save className="w-4 h-4 mr-1" /> Speichern
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Beschreibung */}
      {aufgabe.beschreibung && !editing && (
        <Card>
          <CardContent className="pt-4">
            <p className="text-sm text-slate-700 whitespace-pre-wrap">{aufgabe.beschreibung}</p>
          </CardContent>
        </Card>
      )}

      {/* Hauptaufgabe/Unteraufgaben (Checkliste) + Kommunikationsentwuerfe */}
      {!editing && <AutopilotBefundKarte aufgabeId={aufgabe.id} />}
      {!editing && <UnteraufgabenKarte aufgabe={aufgabe} onChanged={loadAufgabe} />}

      {/* Quelldokumente - Prominent anzeigen */}
      {dateiReferenzen.length > 0 && !editing && (
        <Card className="border-violet-200 bg-violet-50/30" data-testid="quell-dokumente-card">
          <CardContent className="pt-4">
            <h4 className="text-xs font-semibold text-violet-600 uppercase mb-2 flex items-center gap-1.5">
              <FileText className="w-3.5 h-3.5" />
              Quelldokumente ({dateiReferenzen.length})
            </h4>
            <div className="space-y-1.5">
              {dateiReferenzen.map((ref, i) => (
                <div key={i} className="flex items-center justify-between p-2.5 bg-white rounded-lg border border-violet-100 hover:border-violet-300 transition-colors group"
                  data-testid={`quell-dok-${i}`}
                >
                  <div className="flex items-center gap-2.5 min-w-0 flex-1 cursor-pointer"
                    onClick={async () => {
                      try {
                        const url = await holeAktenDateiUrl(aufgabe?.klient_id, ref.ordner, ref.dateiname);
                        setPreviewUrl(url);
                        setPreviewName(ref.dateiname);
                      } catch (e) { toast.error('Vorschau konnte nicht geladen werden'); }
                    }}
                  >
                    <FileText className="w-4 h-4 text-violet-500 shrink-0" />
                    <div className="min-w-0">
                      {renamingIdx === `ref-${i}` ? (
                        <form className="flex items-center gap-1" onSubmit={async (e) => {
                          e.preventDefault();
                          try {
                            const fd = new FormData();
                            fd.append('neuer_name', newName);
                            const res = await fetch(`${API}/api/akten-ordner/umbenennen/${aufgabe.klient_id}/${ref.ordner}/${encodeURIComponent(ref.dateiname)}`, { method: 'POST', body: fd });
                            if (res.ok) {
                              // Auch in der Aufgabe aktualisieren
                              const newRefs = [...dateiReferenzen];
                              newRefs[i] = { ...ref, dateiname: newName };
                              setDateiReferenzen(newRefs);
                              await fetch(`${API}/api/betreuung/aufgaben/${aufgabe.id}`, {
                                method: 'PATCH', headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ datei_referenzen: newRefs })
                              });
                              toast.success('Datei umbenannt');
                            } else toast.error('Umbenennen fehlgeschlagen');
                          } catch { toast.error('Fehler beim Umbenennen'); }
                          setRenamingIdx(null);
                        }}>
                          <Input value={newName} onChange={e => setNewName(e.target.value)} className="h-7 text-sm" autoFocus data-testid={`rename-input-${i}`} />
                          <Button type="submit" size="sm" variant="ghost" className="h-7 px-1.5"><Save className="w-3.5 h-3.5 text-green-600" /></Button>
                          <Button type="button" size="sm" variant="ghost" className="h-7 px-1.5" onClick={() => setRenamingIdx(null)}><X className="w-3.5 h-3.5" /></Button>
                        </form>
                      ) : (
                        <p className="text-sm font-medium text-slate-800 truncate group-hover:text-violet-700">{ref.dateiname}</p>
                      )}
                      <p className="text-xs text-slate-400">Ordner: {ref.ordner}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-0.5 shrink-0">
                    <Button variant="ghost" size="sm" className="opacity-60 group-hover:opacity-100 h-7 w-7 p-0"
                      onClick={async () => {
                        try {
                          const url = await holeAktenDateiUrl(aufgabe?.klient_id, ref.ordner, ref.dateiname);
                          setPreviewUrl(url); setPreviewName(ref.dateiname);
                        } catch (e) { toast.error('Vorschau konnte nicht geladen werden'); }
                      }}
                      data-testid={`quell-dok-preview-${i}`}
                    ><Eye className="w-3.5 h-3.5 text-violet-600" /></Button>
                    <Button variant="ghost" size="sm" className="opacity-60 group-hover:opacity-100 h-7 w-7 p-0"
                      onClick={() => { setRenamingIdx(`ref-${i}`); setNewName(ref.dateiname); }}
                      data-testid={`quell-dok-rename-${i}`}
                    ><Pencil className="w-3.5 h-3.5 text-slate-500" /></Button>
                    <Button variant="ghost" size="sm" className="opacity-60 group-hover:opacity-100 h-7 w-7 p-0"
                      onClick={async () => {
                        try {
                          const url = await holeAktenDateiUrl(aufgabe?.klient_id, ref.ordner, ref.dateiname);
                          window.open(url, '_blank');
                        } catch (e) { toast.error('Download nicht möglich'); }
                      }}
                      data-testid={`quell-dok-download-${i}`}
                    ><Download className="w-3.5 h-3.5 text-violet-600" /></Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* TABS */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="verlauf" data-testid="tab-verlauf">
            <Clock className="w-4 h-4 mr-1" /> Verlauf
          </TabsTrigger>
          <TabsTrigger value="unterlagen" data-testid="tab-unterlagen">
            <FileText className="w-4 h-4 mr-1" /> Unterlagen ({dokumente.length + briefe.length + dateiReferenzen.length})
          </TabsTrigger>
          <TabsTrigger value="verknuepfte" data-testid="tab-verknuepfte">
            <Link2 className="w-4 h-4 mr-1" /> Verknüpft ({verknuepfte.length})
          </TabsTrigger>
        </TabsList>

        {/* ===== VERLAUF TAB ===== */}
        <TabsContent value="verlauf" className="mt-4 space-y-4">
          {/* Neuer Eintrag */}
          <Card>
            <CardContent className="pt-4">
              <div className="flex gap-2">
                <Input
                  type="date"
                  value={verlaufDatum}
                  onChange={e => setVerlaufDatum(e.target.value)}
                  className="w-36 shrink-0"
                />
                <Input
                  placeholder="Neuen Verlaufseintrag hinzufügen..."
                  value={verlaufText}
                  onChange={e => setVerlaufText(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && addVerlaufEintrag()}
                  data-testid="verlauf-input"
                />
                <Button size="sm" onClick={addVerlaufEintrag} disabled={addingVerlauf || !verlaufText.trim()} data-testid="verlauf-add-btn">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Timeline */}
          <div className="space-y-0 ml-4 border-l-2 border-slate-200">
            {verlauf.length > 0 ? verlauf.map((v, i) => (
              <div
                key={v.id || i}
                className={`relative pl-6 pb-4 ${(v.typ === 'dokument' || v.dokument_id || v.brief_id) ? 'cursor-pointer hover:bg-slate-50 rounded-r-lg transition-colors' : ''}`}
                data-testid={`verlauf-entry-${i}`}
                onClick={() => {
                  if (v.typ === 'dokument' || v.dokument_id || v.brief_id) {
                    setActiveTab('unterlagen');
                  }
                }}
              >
                <div className={`absolute -left-[9px] top-1 w-4 h-4 rounded-full border-2 border-white ${
                  v.typ === 'status' ? 'bg-blue-500' :
                  v.typ === 'dokument' ? 'bg-green-500' :
                  v.typ === 'manuell' ? 'bg-slate-400' :
                  'bg-slate-300'
                }`} />
                <div className="flex items-start gap-3">
                  <span className="text-xs text-slate-400 w-20 shrink-0 pt-0.5">
                    {v.datum ? new Date(v.datum).toLocaleDateString('de-DE') : ''}
                  </span>
                  <div className="flex-1">
                    <p className="text-sm text-slate-700">{v.text}</p>
                    {(v.typ === 'dokument' || v.dokument_id || v.brief_id) && (
                      <p className="text-xs text-blue-500 mt-0.5 flex items-center gap-1">
                        <FileText className="w-3 h-3" /> Unterlage anzeigen
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )) : (
              <div className="pl-6 py-4">
                <p className="text-sm text-slate-400">Noch keine Verlaufseinträge</p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* ===== UNTERLAGEN TAB ===== */}
        <TabsContent value="unterlagen" className="mt-4 space-y-4">
          <div className="flex items-center gap-2 flex-wrap">
            <Button size="sm" variant="outline" data-testid="brief-erstellen-btn"
              onClick={() => openTab(schreibstubeUrl({ klient: aufgabe.klient_id, aufgabe: id }), { title: 'Korrespondenz' })}>
              <BookOpen className="w-4 h-4 mr-1" /> Brief erstellen
            </Button>
            <Button size="sm" variant="outline" onClick={() => setShowDateiLink(!showDateiLink)} data-testid="datei-verknuepfen-btn">
              <FileText className="w-4 h-4 mr-1" /> Datei verknuepfen
            </Button>
          </div>

          {/* Datei-Verknüpfen Dialog */}
          {showDateiLink && aufgabe?.klient_id && (
            <Card className="border-blue-200 bg-blue-50/50">
              <CardContent className="py-3 space-y-3">
                <p className="text-sm font-medium text-slate-700">Datei aus Klientenordner verknuepfen</p>
                <div className="flex gap-2">
                  <select
                    className="flex-1 text-sm border border-slate-300 rounded-lg px-3 py-2"
                    value={linkOrdner}
                    onChange={async (e) => {
                      setLinkOrdner(e.target.value);
                      if (e.target.value) {
                        try {
                          const res = await fetch(`${API}/api/akten-ordner/dateien/${aufgabe.klient_id}/${e.target.value}`, { headers: { 'Authorization': `Bearer ${token}` } });
                          const data = await res.json();
                          setOrdnerDateien(data.dateien || []);
                        } catch { setOrdnerDateien([]); }
                      }
                    }}
                    data-testid="link-ordner-select"
                  >
                    <option value="">Ordner waehlen...</option>
                    {ordnerListe.map(o => (
                      <option key={o.name} value={o.name}>{o.label} ({o.dateien_anzahl})</option>
                    ))}
                  </select>
                  <Button variant="ghost" size="sm" onClick={() => { setShowDateiLink(false); setLinkOrdner(''); setOrdnerDateien([]); }}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
                {ordnerDateien.length > 0 && (
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {ordnerDateien.map((d, i) => (
                      <button
                        key={i}
                        className="w-full text-left px-3 py-2 rounded-lg hover:bg-blue-100 text-sm flex items-center justify-between"
                        onClick={async () => {
                          try {
                            await fetch(`${API}/api/betreuung/aufgaben/${id}/datei-verknuepfen`, {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                              body: JSON.stringify({ ordner: linkOrdner, dateiname: d.name })
                            });
                            toast.success(`${d.name} verknuepft`);
                            setDateiReferenzen(prev => [...prev, { ordner: linkOrdner, dateiname: d.name }]);
                            setShowDateiLink(false);
                            setLinkOrdner('');
                            setOrdnerDateien([]);
                          } catch (e) { toast.error('Fehler'); }
                        }}
                        data-testid={`link-datei-${d.name}`}
                      >
                        <span className="truncate">{d.name}</span>
                        <span className="text-xs text-slate-400 shrink-0 ml-2">{(d.groesse / 1024).toFixed(0)} KB</span>
                      </button>
                    ))}
                  </div>
                )}
                {linkOrdner && ordnerDateien.length === 0 && (
                  <p className="text-xs text-slate-400">Keine Dateien in diesem Ordner</p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Physische Datei-Referenzen */}
          {dateiReferenzen.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-slate-500 uppercase mb-2">Verknuepfte Dateien</h4>
              <div className="space-y-2">
                {dateiReferenzen.map((ref, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-violet-50 rounded-lg border border-violet-200" data-testid={`datei-ref-${i}`}>
                    <div className="flex items-center gap-3">
                      <FileText className="w-4 h-4 text-violet-600 shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-slate-800">{ref.dateiname}</p>
                        <p className="text-xs text-slate-500">Ordner: {ref.ordner}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost" size="sm"
                        onClick={async () => {
                          try {
                            const url = await holeAktenDateiUrl(aufgabe?.klient_id, ref.ordner, ref.dateiname);
                            window.open(url, '_blank');
                          } catch (e) { toast.error('Download nicht möglich'); }
                        }}
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost" size="sm" className="text-red-400 hover:text-red-600"
                        onClick={async () => {
                          try {
                            await fetch(`${API}/api/betreuung/aufgaben/${id}/datei-entknuepfen?ordner=${ref.ordner}&dateiname=${encodeURIComponent(ref.dateiname)}`, {
                              method: 'DELETE',
                              headers: { 'Authorization': `Bearer ${token}` }
                            });
                            setDateiReferenzen(prev => prev.filter((_, j) => j !== i));
                            toast.success('Verknuepfung entfernt');
                          } catch { toast.error('Fehler'); }
                        }}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Briefe */}
          {briefe.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-slate-500 uppercase mb-2">Briefe</h4>
              <div className="space-y-2">
                {briefe.map(b => (
                  <div key={b.id} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200" data-testid={`brief-${b.id}`}>
                    <div className="flex items-center gap-3">
                      <BookOpen className="w-4 h-4 text-blue-600 shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-slate-800">{b.betreff}</p>
                        <p className="text-xs text-slate-500">An {b.empfaenger_name} - {b.datum}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={b.status === 'versendet' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}>
                        {b.status}
                      </Badge>
                      {/* derselbe EINE Arbeitsplatz: alter Entwurf wird dabei verlustfrei uebernommen (gleicher Datensatz) */}
                      {(b.quelle === 'schreibstube' || b.status === 'entwurf' || !b.status) && <Button variant="ghost" size="sm" data-testid={`brief-korrespondenz-${b.id}`} title="In Korrespondenz öffnen" onClick={async () => {
                        try {
                          if (b.quelle !== 'schreibstube' && (b.status === 'entwurf' || !b.status)) await schreibstubeAPI.uebernehmen(b.id);
                          openTab(schreibstubeUrl({ brief: b.id }), { title: 'Korrespondenz' });
                        } catch (e) { toast.error(e.message); }
                      }}>
                        <Mail className="w-4 h-4" />
                      </Button>}
                      <Button variant="ghost" size="sm" onClick={async () => {
                        try {
                          const url = await holeBriefPdfUrl(b.id);
                          window.open(url, '_blank');
                        } catch (e) { toast.error('Download nicht möglich'); }
                      }}>
                        <Download className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* DB-Dokumente */}
          {dokumente.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-slate-500 uppercase mb-2">Dokumente</h4>
              <div className="space-y-2">
                {dokumente.map(d => (
                  <div key={d.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-testid={`dok-${d.id}`}>
                    <div className="flex items-center gap-3">
                      <FileText className="w-4 h-4 text-slate-500 shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-slate-800">{d.titel || d.dateiname}</p>
                        <p className="text-xs text-slate-500">{d.beschreibung || d.dokumententyp || ''}</p>
                      </div>
                    </div>
                    <span className="text-xs text-slate-400">{d.created_date?.slice(0, 10)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {dokumente.length === 0 && briefe.length === 0 && dateiReferenzen.length === 0 && (
            <p className="text-center text-slate-400 py-8">Keine Unterlagen verknuepft</p>
          )}
        </TabsContent>

        {/* ===== VERKNÜPFTE AUFGABEN TAB ===== */}
        <TabsContent value="verknuepfte" className="mt-4 space-y-4">
          <div>
            {!showLinkSearch ? (
              <Button size="sm" variant="outline" onClick={() => setShowLinkSearch(true)} data-testid="link-aufgabe-btn">
                <Plus className="w-4 h-4 mr-1" /> Aufgabe verknüpfen
              </Button>
            ) : (
              <Card>
                <CardContent className="pt-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <Input
                      placeholder="Aufgabe suchen..."
                      value={linkQuery}
                      onChange={e => searchAufgaben(e.target.value)}
                      autoFocus
                      data-testid="link-search-input"
                    />
                    <Button variant="ghost" size="sm" onClick={() => { setShowLinkSearch(false); setLinkQuery(''); }}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                  {alleAufgaben.length > 0 && (
                    <div className="max-h-48 overflow-y-auto space-y-1">
                      {alleAufgaben.map(a => (
                        <button
                          key={a.id}
                          onClick={() => linkAufgabe(a.id)}
                          className="w-full text-left p-2 rounded-lg hover:bg-blue-50 text-sm flex items-center justify-between"
                          data-testid={`link-option-${a.id}`}
                        >
                          <span className="truncate">{a.titel}</span>
                          <span className="text-xs text-slate-400 shrink-0 ml-2">{a.betreuter_name || ''}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {verknuepfte.length > 0 ? (
            <div className="space-y-2">
              {verknuepfte.map(a => {
                const s = STATUS_CONFIG[a.status] || STATUS_CONFIG.offen;
                return (
                  <div
                    key={a.id}
                    className="p-3 rounded-lg bg-slate-50 hover:bg-blue-50 cursor-pointer transition-colors border border-transparent hover:border-blue-200 group"
                    onClick={() => openTab(`/aufgaben/${a.id}`, a.titel)}
                    data-testid={`verknuepfte-${a.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-slate-800 group-hover:text-blue-800">{a.titel}</p>
                        <p className="text-xs text-slate-500">{a.betreuter_name || ''}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={`text-xs ${s.color}`}>{s.label}</Badge>
                        <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-blue-500" />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-center text-slate-400 py-8">Keine verknüpften Aufgaben</p>
          )}
        </TabsContent>
      </Tabs>

      {/* PDF-Vorschau Modal */}
      {previewUrl && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="pdf-preview-modal"
          onClick={(e) => { if (e.target === e.currentTarget) { setPreviewUrl(null); setPreviewName(''); } }}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl h-[85vh] flex flex-col">
            <div className="px-4 py-3 border-b flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2 min-w-0">
                <FileText className="w-4 h-4 text-violet-500 shrink-0" />
                <p className="text-sm font-medium text-slate-800 truncate">{previewName}</p>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="sm" onClick={() => window.open(previewUrl, '_blank')} data-testid="preview-download-btn">
                  <Download className="w-4 h-4 mr-1" /> Herunterladen
                </Button>
                <Button variant="ghost" size="sm" onClick={() => { setPreviewUrl(null); setPreviewName(''); }}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <div className="flex-1 overflow-hidden">
              {previewName.toLowerCase().endsWith('.pdf') ? (
                <iframe src={previewUrl} className="w-full h-full border-0" title={previewName} />
              ) : previewName.match(/\.(jpg|jpeg|png|gif|webp|bmp|tiff?)$/i) ? (
                <div className="w-full h-full flex items-center justify-center bg-slate-100 p-4">
                  <img src={previewUrl} alt={previewName} className="max-w-full max-h-full object-contain" />
                </div>
              ) : previewName.match(/\.(txt|md|log|csv|html|xml|json)$/i) ? (
                <iframe src={previewUrl} className="w-full h-full border-0 bg-white" title={previewName} />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-slate-50">
                  <div className="text-center space-y-3">
                    <FileText className="w-12 h-12 text-slate-300 mx-auto" />
                    <p className="text-sm text-slate-500">Vorschau nicht verfuegbar fuer diesen Dateityp</p>
                    <Button variant="outline" onClick={() => window.open(previewUrl, '_blank')}>
                      <Download className="w-4 h-4 mr-1" /> Datei herunterladen
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
